import {
    useMock,
    corporateGet,
    corporatePut,
    corporatePost,
    corporateDelete,
} from './client';
import usersMock from '@/mocks/corporate/users';
import api from '@/utils/api';
import { isCorporateEnvironment } from './environment';

/** Tipos de usuário no corporativo: gestor = 2, colaborador = 3. */
export const CORPORATE_USER_TYPE = {
    GESTOR: 2,
    COLABORADOR: 3,
};

export function isCorporateManagerType(type) {
    return Number(type) === CORPORATE_USER_TYPE.GESTOR;
}

export function isCorporateCollaboratorType(type) {
    return Number(type) === CORPORATE_USER_TYPE.COLABORADOR;
}

export function canMarkCollaboratorResponsible(user) {
    return Boolean(user?.sectorId && user?.roleId);
}

/** Aceita 2 ou 3; type 0 legado vira colaborador. */
export function resolveCorporateUserType(value, fallback = CORPORATE_USER_TYPE.COLABORADOR) {
    const n = Number(value);
    if (n === CORPORATE_USER_TYPE.GESTOR) return CORPORATE_USER_TYPE.GESTOR;
    if (n === CORPORATE_USER_TYPE.COLABORADOR) return CORPORATE_USER_TYPE.COLABORADOR;
    return fallback;
}

/** Mantém 1 (super admin), 2 e 3. Type 0 legado continua como colaborador. */
function listedUserType(value) {
    const n = Number(value);
    if (n === 1 || n === CORPORATE_USER_TYPE.GESTOR || n === CORPORATE_USER_TYPE.COLABORADOR) return n;
    return resolveCorporateUserType(value);
}

function queryCorporateUserType(value) {
    if (value === undefined || value === null || value === '') return undefined;
    const n = Number(value);
    if (n === CORPORATE_USER_TYPE.GESTOR) return CORPORATE_USER_TYPE.GESTOR;
    if (n === CORPORATE_USER_TYPE.COLABORADOR || n === 0) return CORPORATE_USER_TYPE.COLABORADOR;
    return undefined;
}

/**
 * Colaborador responsável pelo setor: `manager === 1`.
 * `0`, `null` ou ausente = não responsável.
 * Se `manager` não vier no DTO (listas corporativas antigas), cai em `isResponsible`.
 */
export function isCollaboratorResponsible(user) {
    if (!user || typeof user !== 'object') return false;
    if (Object.prototype.hasOwnProperty.call(user, 'manager')) {
        const flag = user.manager;
        if (flag === null || flag === '' || flag === false) return false;
        if (typeof flag === 'object') return user.isResponsible === true;
        return Number(flag) === 1;
    }
    return user.isResponsible === true;
}

export function normalizePortalUser(user) {
    if (!user || typeof user !== 'object') return user;
    return {
        ...user,
        isResponsible: isCollaboratorResponsible(user),
    };
}

function parseResponsibleFilter(value) {
    if (value === true || value === 'yes' || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'no' || value === 'false' || value === 0 || value === '0') return false;
    return null;
}

function digitsOnly(value) {
    return String(value ?? '').replace(/\D/g, '');
}

function normalizeStatus(status) {
    if (status === 'active' || status === 'inactive') return status;
    if (status === 1 || status === '1' || status === true || status === 'true') return 'active';
    if (status === 0 || status === '0' || status === false || status === 'false') return 'inactive';
    if (typeof status === 'string' && status.toLowerCase() === 'ativo') return 'active';
    if (typeof status === 'string' && status.toLowerCase() === 'inativo') return 'inactive';
    return status ? 'active' : 'inactive';
}

function normalizeAdmissionDate(value) {
    if (!value) return null;
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toISOString().slice(0, 10);
}

/**
 * Normaliza o DTO do colaborador corporativo para o frontend.
 * Contrato: GET/PUT/POST /corporate/users e /corporate/users/{id}
 */
export function normalizeCorporateUser(user = {}) {
    if (!user || typeof user !== 'object') return user;

    const progress = user.progress && typeof user.progress === 'object'
        ? {
            ...user.progress,
            enrolments: Array.isArray(user.progress.enrolments) ? user.progress.enrolments : [],
        }
        : user.progress;

    return {
        ...user,
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        photo: user.photo || '',
        type: listedUserType(user.type),
        status: normalizeStatus(user.status),
        boardId: user.boardId || null,
        sectorId: user.sectorId || null,
        sectorName: user.sectorName || '',
        roleId: user.roleId || null,
        roleName: user.roleName || '',
        managerUserId: user.managerUserId || null,
        managerName: user.managerName || '',
        admissionDate: normalizeAdmissionDate(user.admissionDate),
        internalCode: user.internalCode || '',
        isResponsible: isCollaboratorResponsible(user),
        progressPercent: Number(user.progressPercent ?? progress?.completionRate ?? 0) || 0,
        gamificationPoints: Number(user.gamificationPoints ?? progress?.gamificationPoints ?? 0) || 0,
        gamificationLevel: user.gamificationLevel || progress?.gamificationLevel || '',
        progress,
        timeline: Array.isArray(user.timeline) ? user.timeline : [],
        // Campos extras (mock / legado de formulário) — preservados se existirem
        socialNumber: user.socialNumber || '',
        idNumber: user.idNumber || '',
        postcode: user.postcode || '',
        address: user.address || '',
        addressNumber: user.addressNumber || '',
        addressComplement: user.addressComplement || '',
        neighborhood: user.neighborhood || '',
        city: user.city || '',
        county: user.county || '',
        country: user.country || '',
    };
}

function extractListContent(res = {}) {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.content)) return res.content;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.users)) return res.users;
    if (Array.isArray(res.items)) return res.items;
    return [];
}

function normalizeListResponse(res = {}) {
    const content = extractListContent(res).map(normalizeCorporateUser);

    return {
        ...res,
        content,
        totalElements: Number(res.totalElements ?? res.total ?? content.length) || 0,
        page: Number(res.page ?? 0) || 0,
        size: Number(res.size ?? content.length) || 0,
        summary: {
            active: Number(res.summary?.active ?? content.filter((u) => u.status === 'active').length) || 0,
            responsible: Number(
                res.summary?.responsible
                ?? content.filter((u) => u.isResponsible).length,
            ) || 0,
            avgProgress: Number(
                res.summary?.avgProgress
                ?? (content.length
                    ? Math.round(
                        content.reduce((sum, u) => sum + (Number(u.progressPercent) || 0), 0)
                        / content.length,
                    )
                    : 0),
            ) || 0,
        },
        coursesAvailable: res.coursesAvailable || [],
        groupsAvailable: res.groupsAvailable || [],
    };
}

/**
 * Payload de escrita alinhado ao schema do Swagger
 * (PUT/POST /corporate/users e PUT /corporate/users/{id}).
 * Inclui dados pessoais do form (CPF, endereço, etc.) — o backend exige CPF na criação.
 */
export function toCorporateUserWritePayload(payload = {}) {
    const next = {
        name: String(payload.name || '').trim(),
        email: String(payload.email || '').trim().toLowerCase(),
        phone: digitsOnly(payload.phone),
        photo: typeof payload.photo === 'string' ? payload.photo : '',
        status: normalizeStatus(payload.status || 'active'),
        boardId: payload.boardId || null,
        sectorId: payload.sectorId || null,
        roleId: payload.roleId || null,
        managerUserId: payload.managerUserId || null,
        admissionDate: payload.admissionDate || null,
        internalCode: payload.internalCode || '',
        manager: canMarkCollaboratorResponsible(payload) && payload.isResponsible ? 1 : 0,
        isResponsible: canMarkCollaboratorResponsible(payload) && Boolean(payload.isResponsible),
        socialNumber: digitsOnly(payload.socialNumber),
        idNumber: payload.idNumber || '',
        postcode: digitsOnly(payload.postcode),
        address: payload.address || '',
        addressNumber: payload.addressNumber || '',
        addressComplement: payload.addressComplement || '',
        neighborhood: payload.neighborhood || '',
        city: payload.city || '',
        county: payload.county || '',
        country: payload.country || 'br',
    };

    if (isCorporateManagerType(payload.type)) {
        next.type = CORPORATE_USER_TYPE.GESTOR;
    } else if (payload.type !== undefined && payload.type !== null && payload.type !== '') {
        next.type = resolveCorporateUserType(payload.type);
    }

    return next;
}

/**
 * Payload de PUT /corporate/users/{id}/organization
 */
export function toOrganizationWritePayload(payload = {}) {
    const body = {
        sectorId: payload.sectorId || null,
        roleId: payload.roleId || null,
        admissionDate: payload.admissionDate || null,
        internalCode: payload.internalCode || '',
        status: payload.status ? normalizeStatus(payload.status) : undefined,
    };
    if (payload.managerUserId !== undefined) {
        body.managerUserId = payload.managerUserId || null;
    }
    return body;
}

function preferMock(options = {}) {
    return useMock() || options.useMock === true;
}

function normalizeTeamMember(row = {}) {
    const enrolments = Array.isArray(row.enrolments) ? row.enrolments : [];
    return {
        ...normalizeCorporateUser(row),
        completed: Number(row.completed ?? 0) || 0,
        inProgress: Number(row.inProgress ?? 0) || 0,
        overdue: Number(row.overdue ?? 0) || 0,
        situation: row.situation || null,
        enrolments,
        updatedAt: row.updatedAt || null,
    };
}

function normalizeTeamMonitoringResponse(res = {}) {
    const content = Array.isArray(res.content)
        ? res.content.map(normalizeTeamMember)
        : [];
    return {
        ...res,
        content,
        totalElements: Number(res.totalElements ?? content.length) || 0,
        page: Number(res.page ?? 0) || 0,
        size: Number(res.size ?? content.length) || 0,
        updatedAt: res.updatedAt || null,
        summary: {
            collaborators: Number(res.summary?.collaborators ?? content.length) || 0,
            averageProgress: Number(res.summary?.averageProgress ?? 0) || 0,
            completedCourses: Number(res.summary?.completedCourses ?? 0) || 0,
            overdueCourses: Number(res.summary?.overdueCourses ?? 0) || 0,
        },
        filterOptions: {
            sectors: Array.isArray(res.filterOptions?.sectors) ? res.filterOptions.sectors : [],
            courses: Array.isArray(res.filterOptions?.courses) ? res.filterOptions.courses : [],
        },
    };
}

/** Só no ambiente corporativo: admin (type 1) ou colaborador com manager=1. */
export function canAccessTeamMonitoring(user) {
    if (!isCorporateEnvironment() || !user) return false;
    if (Number(user.type) === 1) return true;
    return isCollaboratorResponsible(user);
}

export default {
    async list(accessToken, params = {}, options = {}) {
        const type = queryCorporateUserType(params.type);
        const isResponsible = parseResponsibleFilter(params.isResponsible);
        const query = { ...params, type };
        delete query.isResponsible;
        if (preferMock(options)) {
            const res = await usersMock.list({
                ...query,
                isResponsible: isResponsible === null ? undefined : isResponsible,
            });
            return normalizeListResponse(res);
        }
        // GET /corporate/users não filtra por type (swagger: search, status, sectorId, roleId, managerUserId, progress, page, size).
        // Sem status a API inclui exclusão lógica (status 0).
        // "active" vira status 1; qualquer outro valor vira status 2.
        // Responsável (sim/não) não é query da API: filtra o retorno por manager === 1.
        if (!query.status || isResponsible !== null || type != null) {
            return this.listVisible(accessToken, { ...query, isResponsible });
        }
        return this.fetchUsersPage(accessToken, query);
    },

    async fetchUsersPage(accessToken, params = {}) {
        const res = await corporateGet('/corporate/users', accessToken, {
            search: params.search || undefined,
            status: params.status || undefined,
            sectorId: params.sectorId || undefined,
            roleId: params.roleId || undefined,
            managerUserId: params.managerUserId || undefined,
            page: params.page != null ? params.page : undefined,
            size: params.size != null ? params.size : undefined,
        });
        return normalizeListResponse(res);
    },

    async collectByStatus(accessToken, params, status) {
        const pageSize = 500;
        const users = [];
        const seen = new Set();
        for (let page = 0; page < 20; page += 1) {
            const res = await this.fetchUsersPage(accessToken, {
                ...params,
                status,
                page,
                size: pageSize,
            });
            const chunk = res.content || [];
            chunk.forEach((user) => {
                if (!user?.id || seen.has(String(user.id))) return;
                seen.add(String(user.id));
                users.push(user);
            });
            if (chunk.length < pageSize) break;
        }
        return users;
    },

    /**
     * Ativos (status 1) e inativos (status 2), sem quem foi excluído (status 0).
     */
    async listVisible(accessToken, params = {}) {
        const page = params.page != null ? Number(params.page) : 0;
        const size = params.size != null ? Number(params.size) : 20;
        const responsible = parseResponsibleFilter(params.isResponsible);
        const type = queryCorporateUserType(params.type);
        let merged;
        if (params.status) {
            merged = await this.collectByStatus(accessToken, params, params.status);
        } else {
            const [activeUsers, inactiveUsers] = await Promise.all([
                this.collectByStatus(accessToken, params, 'active'),
                this.collectByStatus(accessToken, params, 'inactive'),
            ]);
            merged = [...activeUsers, ...inactiveUsers];
        }
        if (type != null) {
            merged = merged.filter((user) => Number(user.type) === type);
        }
        if (responsible !== null) {
            merged = merged.filter((user) => Boolean(user.isResponsible) === responsible);
        }
        merged.sort((a, b) => (
            String(a.name || '').localeCompare(String(b.name || ''), 'pt', { sensitivity: 'base' })
        ));
        const start = Math.max(page, 0) * (size > 0 ? size : merged.length || 1);
        const content = size > 0 ? merged.slice(start, start + size) : merged;
        const avgProgress = merged.length
            ? Math.round(
                merged.reduce((sum, user) => sum + (Number(user.progressPercent) || 0), 0) / merged.length,
            )
            : 0;
        return {
            content,
            totalElements: merged.length,
            page,
            size: size > 0 ? size : merged.length,
            summary: {
                active: merged.filter((user) => user.status === 'active').length,
                responsible: merged.filter((user) => user.isResponsible).length,
                avgProgress,
            },
            coursesAvailable: [],
            groupsAvailable: [],
        };
    },

    async listAll(accessToken, options = {}) {
        const res = await this.list(accessToken, { page: 0, size: 10000 }, options);
        return res.content || [];
    },

    async getById(accessToken, id, options = {}) {
        if (preferMock(options)) {
            const user = await usersMock.getById(id);
            return normalizeCorporateUser(user);
        }
        const user = await corporateGet(`/corporate/users/${id}`, accessToken);
        return normalizeCorporateUser(user);
    },

    /**
     * Acompanhamento da equipe (portal do gestor/admin).
     * GET /corporate/team-monitoring
     */
    async teamMonitoring(accessToken, params = {}, options = {}) {
        if (preferMock(options)) {
            const res = await usersMock.list({
                managerUserId: params.managerUserId,
                status: 'active',
                search: params.search,
                sectorId: params.previewSectorId || params.sectorId,
                size: params.size || 100,
            });
            const content = (res.content || []).map((user) => {
                const enrolments = user.enrolments || [];
                const completed = enrolments.filter((e) => e.status === 'completed').length;
                const inProgress = enrolments.filter((e) => e.status === 'in_progress').length;
                const overdue = enrolments.filter((e) => e.status === 'overdue').length;
                const progressPercent = Number(user.progressPercent) || 0;
                let situation = 'in_progress';
                if (overdue > 0) situation = 'overdue';
                else if (progressPercent >= 100) situation = 'completed';
                else if (progressPercent < 30) situation = 'attention';
                return normalizeTeamMember({
                    ...user,
                    completed,
                    inProgress,
                    overdue,
                    situation,
                    enrolments,
                });
            });
            return normalizeTeamMonitoringResponse({ content, summary: res.summary });
        }
        const res = await corporateGet('/corporate/team-monitoring', accessToken, {
            search: params.search || undefined,
            sectorId: params.sectorId || undefined,
            courseId: params.courseId || undefined,
            situation: params.situation && params.situation !== 'all' ? params.situation : undefined,
            previewSectorId: params.previewSectorId || undefined,
            previewRoleId: params.previewRoleId || undefined,
            page: params.page != null ? params.page : undefined,
            size: params.size != null ? params.size : undefined,
        });
        return normalizeTeamMonitoringResponse(res);
    },

    /**
     * Detalhe do colaborador no escopo do gestor.
     * GET /corporate/team-monitoring/{id}
     */
    async getTeamMember(accessToken, id, options = {}) {
        if (preferMock(options)) {
            return this.getById(accessToken, id, options);
        }
        const user = await corporateGet(`/corporate/team-monitoring/${id}`, accessToken, {
            previewSectorId: options.previewSectorId || undefined,
            previewRoleId: options.previewRoleId || undefined,
        });
        return normalizeCorporateUser(user);
    },

    async updateOrganization(accessToken, id, payload) {
        const body = toOrganizationWritePayload(payload);
        if (useMock()) {
            return normalizeCorporateUser(await usersMock.updateOrganization(id, body));
        }
        const user = await corporatePut(`/corporate/users/${id}/organization`, body, accessToken);
        return normalizeCorporateUser(user);
    },

    async getProgress(accessToken, id, options = {}) {
        if (preferMock(options)) return usersMock.getProgress(id);
        const user = await this.getById(accessToken, id);
        return user.progress || {
            completed: 0,
            inProgress: 0,
            notStarted: 0,
            overdue: 0,
            completionRate: 0,
            enrolments: [],
        };
    },

    async getTimeline(accessToken, id, options = {}) {
        if (preferMock(options)) return usersMock.getTimeline(id);
        const user = await this.getById(accessToken, id);
        return { content: user.timeline || [] };
    },

    async enrollInCourse(accessToken, id, payload) {
        if (useMock()) return usersMock.enrollInCourse(id, payload);
        return corporatePost(`/corporate/users/${id}/enrolments`, {
            courseId: payload.courseId,
            courseName: payload.courseName,
        }, accessToken);
    },

    /**
     * Preferir `managers` embutido em /organizational-units e /tree.
     * Mantido como fallback legado.
     */
    async listManagers(accessToken) {
        if (useMock()) return usersMock.listManagers();
        try {
            return await corporateGet('/corporate/users/managers', accessToken);
        } catch (e) {
            return { content: [] };
        }
    },

    async create(accessToken, payload) {
        // API exige socialNumber (CPF) e aceita endereço/docs — sempre enviar.
        const body = toCorporateUserWritePayload(payload, { includeExtended: true });
        body.type = isCorporateManagerType(payload.type)
            ? CORPORATE_USER_TYPE.GESTOR
            : CORPORATE_USER_TYPE.COLABORADOR;
        if (useMock()) {
            return normalizeCorporateUser(await usersMock.create({
                ...payload,
                ...body,
            }));
        }
        const user = await corporatePost('/corporate/users', body, accessToken);
        return normalizeCorporateUser(user);
    },

    async update(accessToken, id, payload) {
        const body = toCorporateUserWritePayload(payload, { includeExtended: true });
        if (useMock()) {
            return normalizeCorporateUser(await usersMock.update(id, {
                ...payload,
                ...body,
            }));
        }
        const user = await corporatePut(`/corporate/users/${id}`, body, accessToken);
        return normalizeCorporateUser(user);
    },

    async remove(accessToken, id) {
        if (useMock()) return usersMock.remove(id);
        // Soft delete (status=0) no backend
        return corporateDelete(`/corporate/users/${id}`, accessToken);
    },

    /**
     * Envia e-mail de redefinição de senha (token + link /redefinir-senha-portal).
     */
    async resetPassword(accessToken, id) {
        if (useMock()) {
            await new Promise((resolve) => setTimeout(resolve, 250));
            return { ok: true };
        }
        return corporatePost(`/corporate/users/${id}/reset-password`, {}, accessToken);
    },

    async importCollaborators(accessToken, file) {
        const form = new FormData();
        form.append('file', file);
        const response = await fetch(`${api.defaults.baseURL}/corporate/users/import`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
            body: form,
            credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const error = new Error(data.message || 'Erro ao importar colaboradores.');
            error.response = { status: response.status, data };
            throw error;
        }
        return data;
    },
};
